<template>
	<section class="user-info">
		<div class="user-info__container">
			<Spinner v-if="loading" color="light"></Spinner>
			<h3 class="user-info__name" v-else>Hi {{ name }}</h3>
			<BasicButton @click="close" :bordered="false" :filled="false" class="user-info__close">
				<FontAwesomeIcon :icon="faTimes" color="white" />
			</BasicButton>
		</div>

		<div class="user-info__details" v-if="email">
			<p class="user-info__details-email">{{ email }}</p>
			<a
				v-if="isLoggedIn && isAuthenticatedWithEmail"
				href
				@click.prevent="handleChangeEmail"
				class="user-info__details-change-email"
			>
				<FontAwesomeIcon :icon="faPencilAlt" />
				{{ $t('WEBAPP_ACCOUNTMODAL_EMAIL_CHANGE') }}
			</a>
			<a
				v-if="isLoggedIn && isAuthenticatedWithEmail"
				href
				@click.prevent="handleChangePassword"
				class="user-info__details-change-email"
			>
				<FontAwesomeIcon :icon="faPencilAlt" />
				{{ $t('WEBAPP_LOGINS_EMAIL_ACTION_CHANGE_PASSWORD') }}
			</a>
		</div>
		<div class="user-info__profile">
			<div class="user-info__profile-signed-in" v-if="loggedInProviders.length > 0">
				<span class="user-info__profile-signed-in__title">{{ $t('WEBAPP_LOGGED_IN_WITH') }}</span>
				<div
					v-for="(icon, index) in loggedInProviderIcons"
					:key="index"
					v-html="icon"
					class="user-info__profile-signed-in__icon"
				></div>
			</div>
			<FreemiumFilterBadge v-if="isPremium" />
		</div>
	</section>
</template>

<script setup lang="ts">
import { SOCIAL_ICONS } from '@/constants/social-icons.constant';
import { LoginProvider } from '@/enums/login';
import { VFMHelper } from '@/helpers/vfm/vfm-helper';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { computed, onMounted, ref } from 'vue';
import { faPencilAlt, faTimes } from '@fortawesome/free-solid-svg-icons';
import BasicButton from '@/ui-components/BasicButton.vue';
import FreemiumFilterBadge from '@/components/filter-bar/FreemiumFilterBadge.vue';
import Spinner from '@/ui-components/Spinner.vue';
import { UserAccountEvents } from '@/enums/events';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
const ChangeEmailModal = defineAsyncComponent(() => import('@/components/modals/account/ChangeEmailModal.vue'));
const ChangePasswordModal = defineAsyncComponent(() => import('@/components/modals/account/ChangePasswordModal.vue'));

const emit = defineEmits<{
	(e: 'close'): void;
}>();

const userStore = useUserStore();
const { loadInfo } = userStore;
const { userInfo, loggedInProviders, isPremium, isLoggedIn } = storeToRefs(userStore);

const userEmail = ref(false);
const loading = ref(true);

const name = computed(() => {
	const name: string | undefined = userInfo.value.name;
	if (name !== undefined) {
		return name.charAt(0).toUpperCase() + name.slice(1);
	}
});

const isAuthenticatedWithEmail = computed(() => {
	return userInfo.value.loginProvider === LoginProvider.JUSTWATCH;
});

const email = computed(() => {
	const email = userInfo.value.email;
	if (email) {
		userEmail.value = true;
		return email;
	}
});

function getLoginProviderIcon(provider: LoginProvider) {
	switch (provider) {
		case LoginProvider.GOOGLE:
			return SOCIAL_ICONS.GOOGLE;
		case LoginProvider.FACEBOOK:
			return SOCIAL_ICONS.FACEBOOK;
		case LoginProvider.APPLE:
			return SOCIAL_ICONS.APPLE;
		case LoginProvider.JUSTWATCH:
			return SOCIAL_ICONS.JUSTWATCH;
	}
}

const loggedInProviderIcons = computed(() => {
	return loggedInProviders.value.map((provider: LoginProvider) => getLoginProviderIcon(provider));
});

function handleChangeEmail() {
	ModalHelper.openModal(ChangeEmailModal);
	TrackingHelper.trackEvent(UserAccountEvents.CHANGE_EMAIL_CLICKED, {});
}

function handleChangePassword() {
	ModalHelper.openModal(ChangePasswordModal);
	TrackingHelper.trackEvent(UserAccountEvents.CHANGE_PASSWORD_CLICKED, {});
}

async function close() {
	emit('close');
	VFMHelper.closeById('settings');
}

onMounted(async () => {
	loading.value = true;
	await loadInfo();
	loading.value = false;
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.user-info {
	margin: 0 30px 20px;
	border-bottom: 1px solid $jw-divider-color;
	padding-bottom: 20px;

	@media (max-width: $screen-sm) {
		margin-top: 30px;
	}

	&__container {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	&__name {
		color: $jw-color-white;
		letter-spacing: 0.2px;
		font-size: $font-size-h3;
		margin-bottom: 8px;
	}

	&__close {
		font-size: 20px;
		margin-top: 16px;
		display: inline-block;
	}

	&__details {
		letter-spacing: 0.4px;
		font-size: $font-size-small;
		display: flex;
		flex-wrap: wrap;
	}

	&__details-email {
		font-size: inherit;
		margin-top: 0;
		color: $jw-color-mediumgray--dark;
		display: inline-block;
	}

	&__details-change-email {
		cursor: pointer;
		font-size: inherit;
		margin-bottom: 12px;
		margin-left: 8px;
	}

	&__details-verify {
		margin-inline-start: 15px;
		margin-top: 0;
		display: inline-block;
		font-size: inherit;
		color: $jw-color-gold;
		cursor: pointer;
	}

	&__profile {
		display: flex;
		justify-content: space-between;
		align-items: center;

		.freemium-filter__badge {
			padding: 2px 8px;
			margin-bottom: 12px;
		}
	}

	&__profile-signed-in {
		display: flex;
		align-items: center;
		justify-content: center;
		margin-top: 0;
		font-size: $font-size-small;
		color: $jw-color-mediumgray--dark;
		background: $jw-color-darkblue--light;
		padding: 4px;
		border-radius: $border-radius-base;

		&__title {
			margin-inline-end: 8px;
		}

		&__icon {
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: 18px;
			width: 18px;
			height: 18px;
			border-radius: 50%;
			margin: 0 4px;
		}
	}

	&__profile-edit {
		font-size: 13px;
		color: $jw-color-lightblue;
		cursor: pointer;
	}

	.freemium-subscription-banner {
		margin: 30px 0 20px;
	}
}
</style>
